import { useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import _ from 'lodash';
import { i18n } from '../../../../language';
import { dutyAssignAPI } from '../../../../apis';
import reorder from '../../../../utils/reorder';
import Note from './note';
import Edit from './edit';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: '1.25rem',
  },
  body: {
    minHeight: '4rem',
    padding: '0.938rem',
    backgroundColor: theme.palette.font.white,
    borderRadius: '0.9375rem',
    boxShadow: '0 0.41875rem 1rem 0 rgba(0, 0, 0, 0.04)',
  },
  list: {
    maxHeight: '21.875rem',
    overflowY: 'auto',
    marginBottom: '0.625rem',
    '&>div:last-child': {
      marginBottom: '0',
    },
  },
  button: {
    width: '100%',
    height: '3.75rem',
    borderRadius: '0.5rem',
    border: '0.0625rem solid #c0c6cb',
    backgroundColor: theme.palette.font.white,
    padding: '0 0 0 1.875rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    color: '#c0c6cb',
    fontWeight: '500',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start',
    textTransform: 'inherit',
  },
}));

const FootNoteInput = ({ publishCtrl, btnopen, handleOnclick, setBtnopen, CreateAPI }) => {
  const classes = useStyles();
  if (publishCtrl) {
    return btnopen ? (
      <Button className={classes.button} onClick={handleOnclick} id="DutyAssign-Button-add">
        {i18n.t('DUTYASSIGN_FOOTNOTE_INPUT')}
      </Button>
    ) : (
      <Edit setBtnopen={setBtnopen} add={true} handleAdd={CreateAPI} text="" />
    );
  }
  return null;
};

export default function Footnote({
  publishCtrl,
  list,
  templateRosterId,
  getFootNoteAPI,
  handleReOrderFootNoteAPI,
  noteList,
}) {
  const classes = useStyles();
  const [btnopen, setBtnopen] = useState(true);

  // create note API
  const CreateAPI = async (text,rowIndex=-1) => {
    try {
      await dutyAssignAPI.postCreateDaFootNote({
        templateRosterId: templateRosterId,
        footnoteName: text,
        footnoteDesc: null,
      });
      setBtnopen(true);
      if(rowIndex>=0){
        list[rowIndex].id=0;
      }
      await getFootNoteAPI(list);
    } catch (error) {
      console.log('-----Create Note error----->', error);
    }
  };

  // delete note API
  const DeleteAPI = async (id,rowIndex=-1) => {
    try {
      if(id>0)
      await dutyAssignAPI.postDeleteDaFootNote({ id });
      if(rowIndex>=0){
        list[rowIndex].id=0;
      }
      await getFootNoteAPI(list);
    } catch (error) {
      console.log('-----Delete Note error----->', error);
    }
  };

  // update note API
  const UpdateAPI = async (id, text) => {
    try {
      await dutyAssignAPI.postUpdateDaFootNote({ footNoteId: id, footnoteName: text, footnoteDesc: null });
      await getFootNoteAPI();
    } catch (error) {
      console.log('-----Update Note error----->', error);
    }
  };

  // pin footnote API
  const pinFootNoteAPI = async (id, pinType) => {
    try {
      await dutyAssignAPI.postPinFootNote({ footNoteId: id, pinType });
      await getFootNoteAPI();
    } catch (error) {
      console.log('-----Pin FootNote error----->', error);
    }
  };

  const handleOnclick = () => {
    setBtnopen(false);
  };

  const handleDragEnd = useCallback(
    (e) => {
      if (!e.destination || e.source.index === e.destination.index) {
        return;
      }
      const newList = reorder(noteList, e.source.index, e.destination.index);
      handleReOrderFootNoteAPI(
        _.map(newList, (item) => ({
          pin: item.pin,
          footNoteId: item.dutyFootnoteId,
        }))
      );
    },
    [noteList]
  );

  return !publishCtrl && list.length === 0 ? null : (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={classes.root}>
        <div className={classes.body}>
          <div className={classes.list}>
            <Droppable droppableId="duty_assgin_note">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {list.map((note, index) => (
                    <Draggable key={note.id} draggableId={note.id.toString()} index={index}>
                      {(provided) => (
                        <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                          <Note
                            name={note.name}
                            publishCtrl={publishCtrl}
                            key={note.id}
                            id={note.id}
                            pin={note.pin}
                            type={note.type}
                            handleDelete={DeleteAPI}
                            handleUpdate={UpdateAPI}
                            handleCreate={CreateAPI}
                            handlePin={pinFootNoteAPI}
                            rowIndex={index}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
          <FootNoteInput
            publishCtrl={publishCtrl}
            btnopen={btnopen}
            handleOnclick={handleOnclick}
            setBtnopen={setBtnopen}
            CreateAPI={CreateAPI}
          />
        </div>
      </div>
    </DragDropContext>
  );
}
